import { useActualIncidentId } from './commonHooks';
import axios, { AxiosError } from 'axios';
import apiConfig from '../../../constants/apiConfig';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { keys as safetyMessageKeys } from './useCreateSafetyMessage';
import { getSafetyMessagesByIncidentId } from './useSafetyMessages';
import {
  EnvironmentalHazard,
  HAZMATClassification,
  IncidentNeed,
  OtherHazard,
  PhysicalHazard,
  PotentialHazard,
  RequiredPPE,
} from '../initialResponsePage/SafetyAnalysis/types';
import { SafetyAnalysisFormState } from '../initialResponsePage/SafetyAnalysis/SafetyAnalysis';
import {
  useAppSelector,
  selectUserGuid,
} from '../../../slices/commonSelectors';
import { UpdateSafetyMessageRequestBody } from '../../../types/iapBaseTypes';
import { HazardousMaterialsFormState } from '../planningView/widgets/HazardousMaterials/HazardousMaterials';

export const safetyAnalysisKeys = {
  hazmats: ['hazmats'] as const,
  hazmatsByIncidentId: (incidentId: string) =>
    [...safetyAnalysisKeys.hazmats, incidentId] as const,
  incidentInfo: ['incidentInfo'] as const,
  incidentInfoByIncidentId: (incidentId: string) =>
    [...safetyAnalysisKeys.incidentInfo, incidentId] as const,
} as const;

export type Material = {
  id: string;
  materialName: string | null;
  quantity: number | null;
  physState: string | null;
  niosh: string | null;
  specificGravity: string | null;
  ph: number | null;
  idlh: string | null;
  flashPoint: string | null;
  lel: string | null;
  uel: string | null;
};

export type CreateHazmatRequest = {
  incidentId: string;
  classifications: HAZMATClassification[];
  otherClassification: string | null;
  incidentNeeds: IncidentNeed[];
  airMonitoring: boolean | null;
  sops: string | null;
  decontaminationProcedure: string | null;
  medicalMonitoring: boolean;
  medicalTreatment: boolean;
  emergencyProcedures: string | null;
  material: Material[] | null;
  additionalProductInfo: string | null;
  hazardMonitoringComments: string | null;
  lelInstruments: string | null;
  o2Instruments: string | null;
  radiologicalInstruments: string | null;
  toxicityInstruments: string | null;
};

export type GetHazmatResponse = CreateHazmatRequest & {
  id: string;
  version: number;
};
type GetHazmats = (incidentId: string) => Promise<GetHazmatResponse>;

const getHazmats: GetHazmats = async (incidentId: string) => {
  const response = await axios.get<GetHazmatResponse>(
    `${apiConfig.apiGateway.ics_hazmats}/${incidentId}`
  );
  return response.data;
};

export const useGetHazmats = () => {
  const incidentId = useActualIncidentId();
  return useQuery({
    queryKey: safetyAnalysisKeys.hazmatsByIncidentId(incidentId),
    queryFn: () => getHazmats(incidentId),
    enabled: Boolean(incidentId),
  });
};

export type Weather = {
  temp?: string | null;
  conditions?: string | null;
  wind?: string | null;
  tides?: string | null;
  seaState?: string | null;
  waterTemp?: string | null;
  forecast?: string | null;
};
export type IncidentInfoRequest = {
  userGuid: string;
  situationSummary?: string | null;
  operationalPeriodCommandEmphasis?: string | null;
  specialMedicalEmergencyProcedures?: string | null;
  medicalAirAssetsYn?: boolean | null;
  mapSketchUrl?: string | null;
  safetyPlanUrl?: string | null;
  priorities?: string | null;
  keyDecisionsAndProcedures?: string | null;
  limitationsAndConstraints?: string | null;
  criticalInformationRequirements?: string | null;
  incidentLocation?: string | null;
  communicationsSpecialInstructions?: string | null;
  communityLifelines?: string[] | null;
  siteSafetyPlan?: boolean | null;
  siteSafetyPlanLocation?: string | null;
  physicalHazards?: PhysicalHazard[] | null;
  environmentalHazards?: EnvironmentalHazard[] | null;
  potentialHazards?: PotentialHazard[] | null;
  otherHazards?: OtherHazard[] | null;
  requiredPpe?: RequiredPPE[] | null;
  notesOnPpe?: string | null;
  weather?: Weather | null;
};
export type IncidentInfoResponse = IncidentInfoRequest & {
  id: string;
  incidentId: string;
  version: number;
  timestamp: Date;
};

type GetIncidentInfo = (incidentId: string) => Promise<IncidentInfoResponse>;

const getIncidentInfo: GetIncidentInfo = async (incidentId: string) => {
  const response = await axios.get<IncidentInfoResponse>(
    `${apiConfig.apiGateway.ics_incident_info}/${incidentId}`
  );
  return response.data;
};

export const useGetIncidentInfo = () => {
  const incidentId = useActualIncidentId();
  return useQuery({
    queryKey: safetyAnalysisKeys.incidentInfoByIncidentId(incidentId),
    queryFn: () => getIncidentInfo(incidentId),
    enabled: Boolean(incidentId),
  });
};

export const useGetSafetyAnalysis = () => {
  const incidentId = useActualIncidentId();
  return useQueries({
    queries: [
      {
        queryKey: safetyAnalysisKeys.hazmatsByIncidentId(incidentId),
        queryFn: () => getHazmats(incidentId),
        enabled: Boolean(incidentId),
        retry: (failureCount, error) => {
          const resourceIsNotFound =
            error instanceof AxiosError && error.status === 404;
          if (resourceIsNotFound) return false;
          return failureCount < 5;
        },
      },
      {
        queryKey: safetyAnalysisKeys.incidentInfoByIncidentId(incidentId),
        queryFn: () => getIncidentInfo(incidentId),
        enabled: Boolean(incidentId),
      },
      {
        queryKey: [safetyMessageKeys.safetyMessages, incidentId],
        queryFn: () => getSafetyMessagesByIncidentId(incidentId),
        enabled: Boolean(incidentId),
      },
    ],
  });
};

const mapToIncidentInfo = (
  form: SafetyAnalysisFormState | HazardousMaterialsFormState,
  userGuid: string
): IncidentInfoRequest => ({
  userGuid,
  physicalHazards:
    'physicalHazards' in form ? (form.physicalHazards ?? null) : null,
  environmentalHazards:
    'environmentalHazards' in form ? (form.environmentalHazards ?? null) : null,
  otherHazards: 'otherHazards' in form ? (form.otherHazards ?? null) : null,
  requiredPpe: 'requiredPPE' in form ? (form.requiredPPE ?? null) : null,
  weather:
    'weatherConditions' in form ? (form.weatherConditions ?? null) : null,
  notesOnPpe: form.notesOnPPE ?? null,
  potentialHazards: form.potentialHazards ?? null,
});

const mapToSafetyMessage = (
  form: SafetyAnalysisFormState,
  incidentId: string
): UpdateSafetyMessageRequestBody => ({
  incidentId,
  safetyOfficer: form.safetyOfficer ?? undefined,
  comments: form.safetyNotes ?? undefined,
});

const mapToHazmats = (
  form: SafetyAnalysisFormState | HazardousMaterialsFormState,
  incidentId: string
): CreateHazmatRequest => ({
  incidentId,
  incidentNeeds: form.incidentNeeds ?? [],
  classifications: form.hazmatClassification ?? [],
  otherClassification: form.otherClassification ?? null,
  airMonitoring: form?.airMonitoringRequired,
  sops: form.standardOperatingProcedures ?? null,
  decontaminationProcedure: form.decontaminationProcedures ?? null,
  medicalMonitoring: form.medicalMonitoringRequired,
  medicalTreatment: form.medicalTreatmentAndTransportInPlace,
  emergencyProcedures: form.emergencyProcedures ?? null,
  material: form.material ?? null,
  additionalProductInfo:
    'additionalProductInfo' in form
      ? (form.additionalProductInfo ?? null)
      : null,
  hazardMonitoringComments:
    'hazardMonitoringComments' in form
      ? (form.hazardMonitoringComments ?? null)
      : null,
  lelInstruments:
    'lelInstruments' in form ? (form.lelInstruments ?? null) : null,
  o2Instruments: 'o2Instruments' in form ? (form.o2Instruments ?? null) : null,
  radiologicalInstruments:
    'radiologicalInstruments' in form
      ? (form.radiologicalInstruments ?? null)
      : null,
  toxicityInstruments:
    'toxicityInstruments' in form ? (form.toxicityInstruments ?? null) : null,
});

export const useUpdateIncidentInfo = (
  formData: SafetyAnalysisFormState | HazardousMaterialsFormState
) => {
  const userGuid = useAppSelector(selectUserGuid);
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation<
    unknown,
    unknown,
    SafetyAnalysisFormState | HazardousMaterialsFormState | void
  >({
    mutationFn: async (override) => {
      const payload = mapToIncidentInfo(override ?? formData, userGuid!);
      const response = await axios.put(
        `${apiConfig.apiGateway.ics_incident_info}/${incidentId}`,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: safetyAnalysisKeys.incidentInfoByIncidentId(incidentId),
      });
    },
  });
};

export const useUpdateSafetyMessageFromSafetyAnalysis = (
  formData: SafetyAnalysisFormState
) => {
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation<
    unknown,
    unknown,
    { safetyMessageId: string; form?: SafetyAnalysisFormState }
  >({
    mutationFn: async ({ safetyMessageId, form }) => {
      const payload = mapToSafetyMessage(form ?? formData, incidentId);
      const response = await axios.put(
        `${apiConfig.apiGateway.ics_safety_messages}/${safetyMessageId}`,
        payload
      );
      return response.data;
    },
    onSuccess: (_, { safetyMessageId }) => {
      void queryClient.invalidateQueries({
        queryKey: [safetyMessageKeys.safetyMessages, incidentId],
      });
      void queryClient.invalidateQueries({
        queryKey: [safetyMessageKeys.safetyMessages, safetyMessageId],
      });
    },
  });
};

export const useCreateSafetyMessageFromSafetyAnalysis = (
  formData: SafetyAnalysisFormState
) => {
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation<
    Array<{ id: string }>,
    unknown,
    SafetyAnalysisFormState | void
  >({
    mutationFn: async (override) => {
      const payload = mapToSafetyMessage(override ?? formData, incidentId);
      const response = await axios.post(
        `${apiConfig.apiGateway.ics_safety_messages}`,
        payload
      );
      return response.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: [safetyMessageKeys.safetyMessages, incidentId],
      });

      if (data?.[0]?.id) {
        void queryClient.invalidateQueries({
          queryKey: [safetyMessageKeys.safetyMessages, data[0].id],
        });
      }
    },
  });
};

export const usePostHazmats = (
  formData: SafetyAnalysisFormState | HazardousMaterialsFormState
) => {
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation<
    unknown,
    unknown,
    SafetyAnalysisFormState | HazardousMaterialsFormState | void
  >({
    mutationFn: async (override) => {
      const payload = mapToHazmats(override ?? formData, incidentId);
      const response = await axios.post(
        `${apiConfig.apiGateway.ics_hazmats}`,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: safetyAnalysisKeys.hazmatsByIncidentId(incidentId),
      });
    },
  });
};

export const useUpdateSafetyAnalysis = (formData: SafetyAnalysisFormState) => {
  const updateIncidentInfo = useUpdateIncidentInfo(formData);
  const createSafetyMessage =
    useCreateSafetyMessageFromSafetyAnalysis(formData);
  const updateSafetyMessage =
    useUpdateSafetyMessageFromSafetyAnalysis(formData);
  const postHazmats = usePostHazmats(formData);

  return {
    updateIncidentInfo,
    createSafetyMessage,
    updateSafetyMessage,
    postHazmats,
  } as const;
};
